import React, { createContext, useContext, useRef, useCallback, useState } from "react";

/**
 * NavigationContext
 *
 * Provides a global navigation system with support for parameters.
 * Components can navigate to pages and pass data (e.g., IDs to open specific modals).
 *
 * Usage in a dashboard:
 *   const { registerNavigator } = useContext(NavigationContext);
 *   useEffect(() => registerNavigator((key) => setSelectedMenu(key)), []);
 *
 * Usage in a consumer (e.g. NotificationBar):
 *   const { navigateTo } = useContext(NavigationContext);
 *   navigateTo("leaves", { leaveId: 4950, openModal: true });
 *
 * Usage in a page component (e.g. Leaves):
 *   const { navigationParams, clearNavigationParams } = useContext(NavigationContext);
 *   useEffect(() => {
 *     if (navigationParams?.leaveId) {
 *       // Open modal with leaveId
 *       clearNavigationParams();
 *     }
 *   }, [navigationParams]);
 */
export const NavigationContext = createContext({
  navigateTo: () => {},
  registerNavigator: () => {},
  navigationParams: null,
  clearNavigationParams: () => {},
});

export const NavigationProvider = ({ children }) => {
  // Use a ref so the latest registered navigator is always available
  // without causing re-renders when it changes
  const navigatorRef = useRef(null);
  
  // Store navigation parameters (e.g., { leaveId: 4950, openModal: true })
  // Persist to localStorage to survive cross-dashboard redirects (page reloads)
  const [navigationParams, setNavigationParams] = useState(() => {
    try {
      const saved = localStorage.getItem("bemss_navigation_params");
      return saved ? JSON.parse(saved) : null;
    } catch {
      return null;
    }
  });

  const registerNavigator = useCallback((fn) => {
    console.log("📝 Dashboard registered navigator function");
    navigatorRef.current = fn;
    // Return cleanup so useEffect can unregister on unmount
    return () => {
      console.log("🗑️ Dashboard unregistered navigator function");
      navigatorRef.current = null;
    };
  }, []);

  const navigateTo = useCallback((menuKey, params = null) => {
    console.log("🧭 NavigationContext.navigateTo() called with:", menuKey, params);
    
    // Store parameters for the target page to consume
    if (params) {
      console.log("📦 Setting navigation parameters:", params);
      setNavigationParams(params);
      localStorage.setItem("bemss_navigation_params", JSON.stringify(params));
    }
    
    if (typeof navigatorRef.current === "function") {
      console.log("✅ Navigator function found, calling it...");
      navigatorRef.current(menuKey);
      console.log("✅ Navigator function executed successfully");
    } else {
      console.error("❌ [NavigationContext] No navigator registered!");
      console.log("ℹ️ Make sure the dashboard has called registerNavigator() in useEffect");
    }
  }, []);

  const clearNavigationParams = useCallback(() => {
    console.log("🧹 Clearing navigation parameters");
    setNavigationParams(null);
    localStorage.removeItem("bemss_navigation_params");
  }, []);

  return (
    <NavigationContext.Provider 
      value={{ 
        navigateTo, 
        registerNavigator, 
        navigationParams, 
        clearNavigationParams 
      }}
    >
      {children}
    </NavigationContext.Provider>
  );
};

export const useNavigation = () => useContext(NavigationContext);
